import { useSelector } from "react-redux";
import { RootState } from "../../store";
import useGetFeatureFlagsList from "../../hooks/useGetFeatureFlags";
import { useState, useEffect, useMemo } from "react";
import { FeatureFlagsResponse } from "../../services/getFeatureFlags";
import { toast } from "react-toastify";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";

import { Flex, Button } from "@mantine/core";
import useUpdateFeatureFlags from "../../hooks/useUpdateFeatureFlags";

const FeatureFlag = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const featureFlagMutation = useGetFeatureFlagsList();
  const [data, setData] = useState<FeatureFlagsResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const updateFeatureFlagsMutation = useUpdateFeatureFlags();

  const fetchData = async () => {
    featureFlagMutation.mutate(
      { token },
      {
        onSuccess: (data) => {
          setData(data);
          setIsLoading(false);
        },
        onError: (error) => {
          setIsLoading(false);
          toast.error((error as Error).message);
        },
      }
    );
  };

  const updateData = async (updatedRows: typeof data) => {
    const featureFlagData = updatedRows.map((item) => ({
      id: item.id,
      featureName: item.featureName,
      isEnabled: item.isEnabled,
    }));
    updateFeatureFlagsMutation.mutate(
      {
        token,
        featureFlag: featureFlagData,
      },
      {
        onSuccess: () => {
          toast.info("The feature flags has updated.");
          fetchData();
        },
        onError: (e) => {
          toast.error((e as Error).message);
          fetchData();
        },
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = useMemo<MRT_ColumnDef<FeatureFlagsResponse>[]>(
    () => [
      {
        accessorKey: "featureName",
        header: "Feature Name",
      },
      {
        accessorKey: "isEnabled",
        header: "Enabled",
        Cell: ({ cell }) => (
          <span>{cell.getValue<boolean>() === true ? "On" : "Off"}</span>
        ),
      },
    ],
    [data]
  );

  const table = useMantineReactTable({
    enableColumnFilters: false,
    paginationDisplayMode: "pages",
    state: { isLoading },
    columns,
    data: data,
    initialState: { density: "xs" },
    mantineTableProps: {
      withBorder: true,
    },
    editDisplayMode: "row",
    enableRowSelection: true,
    renderTopToolbar: ({ table }) => {
      const handleDisabled = () => {
        const selectedRows = table
          .getSelectedRowModel()
          .flatRows.map((row) => row.index);

        const updatedRows = selectedRows
          .map((index) => {
            const item = data[index];
            const checkIsEnabled = false;
            if (item && item.isEnabled !== checkIsEnabled) {
              return { ...item, isEnabled: checkIsEnabled };
            }
            return null;
          })
          .filter((item): item is (typeof data)[0] => item !== null);

        const updatedData = data.map((item, index) => {
          if (selectedRows.includes(index) && item.isEnabled) {
            return { ...item, isEnabled: false };
          }
          return item;
        });

        setData(updatedData);

        if (updatedRows.length > 0) {
          updateData(updatedRows);
        }
      };

      const handleEnabled = () => {
        const selectedRows = table
          .getSelectedRowModel()
          .flatRows.map((row) => row.index);

        const updatedRows = selectedRows
          .map((index) => {
            const item = data[index];
            const checkIsEnabled = true;
            if (item && item.isEnabled !== checkIsEnabled) {
              return { ...item, isEnabled: checkIsEnabled };
            }
            return null;
          })
          .filter((item): item is (typeof data)[0] => item !== null);

        const updatedData = data.map((item, index) => {
          if (selectedRows.includes(index)) {
            return { ...item, isEnabled: true };
          }
          return item;
        });
        setData(updatedData);
        if (updatedRows.length > 0) {
          updateData(updatedRows);
        }
      };

      return (
        <Flex p="md" justify="space-between">
          <Flex gap="xs"></Flex>
          <Flex sx={{ gap: "8px" }}>
            <Button
              color="red"
              disabled={
                !(table.getIsAllRowsSelected() || table.getIsSomeRowsSelected())
              }
              onClick={handleDisabled}
              variant="filled"
            >
              Disable
            </Button>
            <Button
              color="green"
              disabled={
                !(table.getIsAllRowsSelected() || table.getIsSomeRowsSelected())
              }
              onClick={handleEnabled}
              variant="filled"
            >
              Enable
            </Button>
          </Flex>
        </Flex>
      );
    },
  });

  return (
    <div>
      <div>
        <MantineReactTable table={table} />
      </div>
    </div>
  );
};

export default FeatureFlag;
